/* eslint-disable jsx-a11y/label-has-associated-control */
import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { Button, Col, Form, Input, Row, Select } from 'antd';
import PropTypes from 'prop-types';

import { DASHBOARD_ITEM_API_NAMES } from '@src/constants/commons';
import CountChartService from '@src/services/CountChartService';

import BoxContainer from 'components/BoxContainer';
import Title from 'components/BoxContainer/components/Title';
import FormSelect from 'components/Form/components/FormSelect';

import { SlidePanelContext } from 'packages/dashboard/components/SlidePanel/SlidePanelContext';
import useGetEventTypes from 'packages/utils/hooks/useGetEventTypes';

import IntlMessages from 'util/IntlMessages';

import DropDownTriggers from '../../../../../../containers/DropDownTriggers';
import {
  DASHBOARDS_EVENT_TYPE,
  DASHBOARDS_FORMS,
  LAYOUT_BY_COMPONENT_TYPE,
} from '../../constants';
import { useEventsByScheduleContext } from '../../contexts/EventsByScheduleContext';
import { DISPLAY_TYPES, secondDimensionOptions } from './constants';

const CountChartForm = forwardRef(({ divId, chartId, boardId }, formRef) => {
  const intl = useIntl();
  const orgId = useSelector(state => state.organizations.organization.id);

  const auxEventType = useRef();
  const internalFormRef = useRef(null);
  const { boardSelected } = useContext(SlidePanelContext);
  const {
    loading: schedulesLoading,
    eventsFromSchedules: eventTypesFromSchedule,
    fetchEventsFromSchedules,
  } = useEventsByScheduleContext();
  const countChartService = useMemo(() => new CountChartService(), []);
  const [formId, setFormId] = useState();
  const [eventIds, setEventIds] = useState([]);
  const [loading, setLoading] = useState(false);

  const eventTypeIdByTriggerSelected = useMemo(
    () => eventIds.map(trigger => trigger?.eventType?.id) ?? [],
    [eventIds],
  );
  const { data: eventTypes, loading: eventTypesLoading } = useGetEventTypes(
    true,
    boardSelected?.divId,
  );

  const mainLoading = schedulesLoading || eventTypesLoading || loading;
  const eventTypesWithNumComponents = useMemo(() => {
    let output = eventTypes;
    if (eventTypeIdByTriggerSelected.length > 0) {
      output = output.filter(et => eventTypeIdByTriggerSelected.includes(et.id));
    }
    return output;
  }, [eventTypes, eventTypeIdByTriggerSelected]);

  const onFormId = et => {
    setFormId(et);
  };

  const onChangeEvents = events => {
    const newEventsSelected = events.map(e => e.id);
    internalFormRef.current.setFieldValue('eventIds', newEventsSelected);
    setEventIds(newEventsSelected);
  };
  const handleClear = useCallback(() => {
    setEventIds([]);
    setFormId(null);

    internalFormRef.current?.resetFields();
  }, []);

  useImperativeHandle(
    formRef,
    () => ({
      ...internalFormRef.current,
      handleClear,
      submit: () => {
        internalFormRef.current?.submit();
      },
    }),
    [handleClear],
  );

  const divisionPerEventType = eventTypes?.find(event => event.id === formId)?.divId || null;

  auxEventType.current =
    formId || (eventTypeIdByTriggerSelected && eventTypesWithNumComponents.length > 0)
      ? {
          value: formId || eventTypeIdByTriggerSelected || null,
        }
      : {};

  useEffect(() => {
    if (formId && orgId) {
      fetchEventsFromSchedules(orgId, divId, formId);
    }
  }, [formId, divId, fetchEventsFromSchedules, orgId]);

  useEffect(() => {
    if (chartId && boardId) {
      const fetchData = async () => {
        setLoading(true);
        const res = await countChartService.getCountChart({ orgId, divId, boardId, chartId });
        if (res) {
          const {
            formId: formIdFetched,
            eventTypes: eventTypesFetched,
            secondDimension,
          } = res.queryParams || {};
          internalFormRef.current.setFieldsValue({
            title: res.title,
            formId: formIdFetched,
            eventIds: eventTypesFetched,
            secondDimension,
            id: res.id,
            layout: res.layout,
            aggregations: res.aggregations,
            metadata: res,
          });
          setFormId(formIdFetched);
        }
        setLoading(false);
      };
      fetchData();
    }
  }, [boardId, divId, orgId, chartId, countChartService]);
  return (
    <Form
      labelCol={{ span: 6 }}
      wrapperCol={{ span: 24 }}
      ref={internalFormRef}
      name={DASHBOARDS_FORMS.EVENT_COUNTER_BY_TIME}
      initialValues={{
        apiName: DASHBOARD_ITEM_API_NAMES.EVENTS_COUNTER,
        layout: LAYOUT_BY_COMPONENT_TYPE[DASHBOARDS_EVENT_TYPE.COUNT_CHARTS],
        aggregations: {
          count: {
            color: '#DDAFFF',
            displayType: 'bar',
          },
        },
      }}
    >
      <BoxContainer content loading={mainLoading}>
        <Row gutter={16}>
          <Col span={24}>
            <Title.LabelForm
              required
              className="gx-guarnic-pb-1"
              value={intl.formatMessage({
                id: 'dashboards.chart.form.title',
              })}
            />
            <Form.Item hidden name="id" />
            <Form.Item hidden name="layout" />
            <Form.Item hidden name="metadata" />
            <Form.Item
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: 'form.required',
                  }),
                },
              ]}
              name="title"
            >
              <Input
                placeholder={intl.formatMessage({
                  id: 'dashboards.chart.form.title.placeholder',
                })}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8} sm={8} xxl={8}>
            <Title.LabelForm required value={<IntlMessages id="dashboards.chart.form.form" />} />
            <Form.Item
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: 'form.required',
                  }),
                },
              ]}
              name="formId"
            >
              <FormSelect
                onChange={val => {
                  onFormId(val);
                  internalFormRef.current.setFieldValue('eventIds', []);
                }}
                options={eventTypesWithNumComponents}
                showSearch
                loading={eventTypesLoading}
                {...auxEventType.current}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8} sm={8} xxl={8}>
            <Title.LabelForm value={<IntlMessages id="general.eventTypes" />} />
            <Form.Item name="eventIds">
              <DropDownTriggers
                mode="multiple"
                onChange={onChangeEvents}
                division={divisionPerEventType}
                eventType={formId}
                value={eventIds}
                defaultValue={eventIds}
                extraTriggers={eventTypesFromSchedule}
                disabled={!formId}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8} sm={8} xxl={8}>
            <Title.LabelForm
              value={intl.formatMessage({
                id: 'general.group.by',
              })}
            />
            <Form.Item name="secondDimension">
              <Select options={secondDimensionOptions} showSearch allowClear />
            </Form.Item>
          </Col>
          <Col xs={24} md={8} sm={8} xxl={8}>
            <Title.LabelForm
              value={intl.formatMessage({
                id: 'dashboards.chart.type',
              })}
            />
            <Form.Item name={['aggregations', 'count', 'displayType']} style={{ marginBottom: 0 }}>
              <Select options={DISPLAY_TYPES} showSearch allowClear />
            </Form.Item>
          </Col>
          <Col className="gx-flex-row gx-justify-content-end gx-mt-4" span={24}>
            <Button onClick={() => handleClear()}>
              <IntlMessages id="button.clear" />
            </Button>
          </Col>
        </Row>
      </BoxContainer>
      <Form.Item hidden name="apiName" />
    </Form>
  );
});

CountChartForm.propTypes = {
  divId: PropTypes.string,
  boardId: PropTypes.string,
  chartId: PropTypes.string,
};

export default CountChartForm;
